import type { ApiFailure } from "@colossus/api-client";
import { demoRefusal } from "./demo/refusals.ts";
import { count, plural } from "./format.ts";
import { getLocale, refusalIn } from "./locale.ts";
import type { Message } from "./locale.ts";
import { m } from "./paraglide/messages.js";

export const commonRefusal = (failure: ApiFailure): string | null => {
  if (failure.status === 401) return m.refusal_signed_out();
  const { code } = failure;
  const demo = demoRefusal(code);
  if (demo !== null) return demo;
  if (failure.status === 413 || code === "body_too_large") return m.refusal_too_large();
  if (code === "discord_rate_limited") {
    const seconds = failure.retryAfterSeconds;
    if (seconds === null) return m.refusal_discord_busy_later();
    const locale = getLocale();
    return plural(locale, seconds, {
      one: m.refusal_discord_busy_one,
      other: m.refusal_discord_busy,
    })({ seconds: count(locale, seconds), n: seconds });
  }
  return null;
};

export const signedOut = (failure: ApiFailure): boolean => failure.status === 401;

export type RefusalTable<C extends string> = Readonly<Partial<Record<C, Message>>>;

export const refusalOf = <C extends string>(
  table: RefusalTable<NoInfer<C>>,
  failure: ApiFailure<C>,
  fallback: Message,
): string =>
  demoRefusal(failure.code) ??
  refusalIn(table as Readonly<Record<string, Message>>, failure.code, fallback)();

export const refusedWith = <C extends string>(failure: ApiFailure<C>, code: NoInfer<C>): boolean =>
  failure.code === code;
